import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { TimePicker } from '@/components/ui/time-picker';
import { toast } from 'sonner';
import { Loader2, Copy, Send, MessageSquare } from 'lucide-react';
import { createDispatchWithOverride } from '../../utils/dispatchAutoBlock';

// Helper function to format date in local timezone (YYYY-MM-DD)
const formatLocalDate = (date) => {
  if (!date) return '';
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to parse date string and extract local date
const parseLocalDate = (dateStr) => {
  if (!dateStr) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
  const d = new Date(dateStr);
  return formatLocalDate(d);
};

const STATUS_OPTIONS = [
  'Empty in Customer', 'Available for Pickup', 'Need Appt', 'On the way to Customer',
  'Important Customer', 'Delivered to Customer', 'In Yard', 'Container Returned',
  'On the way to Return', 'Delivered to Exam Location', 'New Shipment',
  'Empty Container in Yard', 'On the way to Pickup', 'Shipment Closed', 'Trouble'
];

// Task types for dispatch
const TASK_TYPES = [
  { value: 'LOAD_OUT_DELIVER', label: 'Load out from terminal and deliver' },
  { value: 'EMPTY_OUT_DELIVER', label: 'Empty out from terminal' },
  { value: 'RETURN_EMPTY', label: 'Return empty from/to' },
  { value: 'RETURN_LOAD', label: 'Return load from/to' },
  { value: 'DELIVER_FROM_TO', label: 'Deliver from/to' }
];

export const DispatchScheduleTab = ({ 
  shipment, 
  drivers = [], 
  terminals = [], 
  yards = [],
  customers = [],
  api,
  onDispatchCreated
}) => {
  const [loading, setLoading] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    driver_id: '',
    phone_number: '',
    task_type: '',
    deliver_to_type: '',
    deliver_to_location: '',
    deliver_to_other: '',
    return_from_type: '',
    return_from_location: '',
    return_from_other: '',
    return_to_type: '',
    return_to_location: '',
    return_to_other: '',
    is_load_or_empty: '',
    dispatch_date: formatLocalDate(new Date()),  // Default to today in local timezone
    dispatch_time_from: '',
    dispatch_time_to: '',
    require_signature: false,
    require_seal_number: false,
    require_chassis_number: false,
    require_container_picture: false,
    require_terminal_ticket: false,
    delivery_type: '',
    customer_hours_from: '',
    customer_hours_to: '',
    customer_remark: ''
  });

  const [dispatchMessage, setDispatchMessage] = useState('');

  // Reset form and initialize with shipment data when shipment changes
  useEffect(() => {
    if (shipment) {
      // Reset all form fields to defaults, then populate from shipment
      const initialData = {
        driver_id: shipment.driver_id || '',
        phone_number: '',
        task_type: '',
        deliver_to_type: '',
        deliver_to_location: '',
        deliver_to_other: '',
        return_from_type: '',
        return_from_location: '',
        return_from_other: '',
        return_to_type: '',
        return_to_location: '',
        return_to_other: '',
        is_load_or_empty: '',
        // Always default to TODAY and empty times on every open so the previous
        // dispatch's date/time don't carry over (the LOAD_OUT_DELIVER task type
        // still auto-fills from appointment_date when selected — see handleChange).
        dispatch_date: formatLocalDate(new Date()),
        dispatch_time_from: '',
        dispatch_time_to: '',
        require_signature: false,
        require_seal_number: false,
        require_chassis_number: false,
        require_container_picture: false,
        require_terminal_ticket: false,
        delivery_type: ''
      };
      
      // If shipment has a driver, get their phone number
      if (shipment.driver_id && drivers?.length > 0) {
        const driver = drivers.find(d => d.id === shipment.driver_id);
        if (driver?.phone) {
          initialData.phone_number = driver.phone;
        }
      }
      
      setFormData(initialData);
      // Reset dispatch message
      setDispatchMessage('');
    }
  }, [shipment?.id, drivers]); // Trigger when shipment ID changes or drivers load

  // Generate dispatch message whenever relevant fields change
  useEffect(() => {
    if (!shipment || !formData.task_type) {
      setDispatchMessage('');
      return;
    }
    const msg = generateDispatchMessage();
    setDispatchMessage(msg);
  }, [formData, shipment]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'driver_id' && value) {
      const driver = drivers?.find(d => d.id === value);
      if (driver?.phone) {
        setFormData(prev => ({ ...prev, phone_number: driver.phone }));
      }
    }
    
    if (field === 'task_type') {
      // Reset location fields when task type changes
      const updates = {
        deliver_to_type: '',
        deliver_to_location: '',
        deliver_to_other: '',
        return_from_type: '',
        return_from_location: '',
        return_from_other: '',
        return_to_type: '',
        return_to_location: '',
        return_to_other: '',
        is_load_or_empty: ''
      };
      
      // For LOAD_OUT_DELIVER, auto-fill with appointment time from the shipment
      if (value === 'LOAD_OUT_DELIVER' && shipment) {
        updates.dispatch_date = shipment.appointment_date ? shipment.appointment_date.split('T')[0] : '';
        updates.dispatch_time_from = shipment.appointment_time_from || '';
        updates.dispatch_time_to = shipment.appointment_time_to || '';
      }
      
      setFormData(prev => ({ ...prev, ...updates }));
    }
  };

  const getTerminalName = (id) => {
    const terminal = terminals?.find(t => t.id === id);
    return terminal?.name || terminal?.code || '';
  };

  const getYardName = (id) => {
    const yard = yards?.find(y => y.id === id);
    return yard?.name || yard?.code || '';
  };

  const getCustomerAddress = () => {
    if (!shipment) return '';
    // Try to find the customer name (warehouse name) so the driver knows WHERE
    // they're going, not just the coordinates.
    let customerName = '';
    if (shipment.customer_id && customers?.length) {
      const cust = customers.find(c => c.id === shipment.customer_id);
      customerName = cust?.name || cust?.company_name || '';
    }
    if (!customerName) {
      customerName = shipment.customer_name || '';
    }

    // First try shipment delivery fields
    const shipmentParts = [shipment.delivery_address, shipment.delivery_city, shipment.delivery_state, shipment.delivery_zip].filter(Boolean);
    let addressStr = '';
    if (shipmentParts.length > 1) {
      addressStr = shipmentParts.join(', ');
    } else if (shipment.customer_id && customers?.length) {
      // Fallback: customer entity's addresses array (default or first)
      const cust = customers.find(c => c.id === shipment.customer_id);
      if (cust?.addresses?.length) {
        const addr = cust.addresses.find(a => a.is_default) || cust.addresses[0];
        const custParts = [addr.address, addr.city, addr.state, addr.zip_code].filter(Boolean);
        if (custParts.length > 0) addressStr = custParts.join(', ');
      }
    }
    if (!addressStr) addressStr = shipmentParts.join(', ');

    // Prefix warehouse/customer name so driver sees WH name + address
    if (customerName && addressStr) return `${customerName}, ${addressStr}`;
    return customerName || addressStr || '';
  };

  const getLocationName = (type, locationId, otherText) => {
    if (type === 'customer_address') return getCustomerAddress();
    if (type === 'yard') return getYardName(locationId);
    if (type === 'terminal') return getTerminalName(locationId);
    if (type === 'other') return otherText;
    return '';
  };

  const formatDispatchDateTime = () => {
    const parts = [];
    if (formData.dispatch_date) {
      // Parse as local date to avoid timezone offset issues
      // Input format is "YYYY-MM-DD" - split and create local date
      const [year, month, day] = formData.dispatch_date.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed
      parts.push(date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric' }));
    }
    if (formData.dispatch_time_from) {
      parts.push(formData.dispatch_time_from);
      if (formData.dispatch_time_to) {
        parts.push(`- ${formData.dispatch_time_to}`);
      }
    }
    return parts.join(' ');
  };

  // Convert 24h time string to 12h format (e.g. "13:30" -> "01:30PM")
  const formatTime12h = (time24) => {
    if (!time24) return '';
    const [h, m] = time24.split(':').map(Number);
    const ampm = h < 12 ? 'AM' : 'PM';
    const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${String(h12).padStart(2, '0')}:${String(m).padStart(2, '0')}${ampm}`;
  };

  // Format date as MM/DD/YY
  const formatDateShort = (dateStr) => {
    if (!dateStr) return '';
    const [year, month, day] = dateStr.split('-');
    return `${month}/${day}/${year.slice(2)}`;
  };

  const generateDispatchMessage = () => {
    if (!shipment) return '';
    
    const container = shipment.container_number || '';
    const size = shipment.container_size || '';
    const pin = shipment.pin || '';
    const ssl = shipment.ssl || '';
    const terminalObj = terminals?.find(t => t.id === shipment.terminal_id);
    const terminalCode = terminalObj?.code || terminalObj?.name || '';
    
    const deliveryParts = [shipment.delivery_address, shipment.delivery_city, shipment.delivery_state, shipment.delivery_zip].filter(Boolean);
    const deliveryAddr = deliveryParts.join(', ');
    let fromLoc = '', toLoc = '';
    switch (formData.task_type) {
      case 'LOAD_OUT_DELIVER':
      case 'EMPTY_OUT_DELIVER':
        fromLoc = terminalCode;
        toLoc = getLocationName(formData.deliver_to_type, formData.deliver_to_location, formData.deliver_to_other) || deliveryAddr;
        break;
      case 'RETURN_EMPTY':
      case 'RETURN_LOAD':
        fromLoc = getLocationName(formData.return_from_type, formData.return_from_location, formData.return_from_other);
        toLoc = getLocationName(formData.return_to_type, formData.return_to_location, formData.return_to_other);
        break;
      case 'DELIVER_FROM_TO':
        fromLoc = getLocationName(formData.return_from_type, formData.return_from_location, formData.return_from_other);
        toLoc = getLocationName(formData.return_to_type, formData.return_to_location, formData.return_to_other);
        break;
      default:
        break;
    }
    
    // Build date/time
    let dateTimePart = '';
    if (formData.dispatch_date) {
      dateTimePart = formatDateShort(formData.dispatch_date);
      if (formData.dispatch_time_from) {
        dateTimePart += ` ${formatTime12h(formData.dispatch_time_from)}`;
        if (formData.dispatch_time_to) dateTimePart += ` - ${formatTime12h(formData.dispatch_time_to)}`;
      }
    }
    
    // Build requirements
    const reqs = [];
    if (formData.delivery_type === 'LIVE_UNLOAD') reqs.push('LIVE UNLOAD');
    if (formData.delivery_type === 'DROP') reqs.push('DROP');
    if (formData.require_signature) reqs.push('Signature (POD)');
    if (formData.require_chassis_number) reqs.push('Chassis#');
    if (formData.require_seal_number) reqs.push('Seal#');
    if (formData.require_container_picture) reqs.push('Container Picture');
    if (formData.require_terminal_ticket) reqs.push('Terminal Ticket');
    
    // Build customer hours string
    let customerHoursPart = '';
    if (formData.customer_hours_from) {
      customerHoursPart = `Hrs ${formatTime12h(formData.customer_hours_from)}`;
      if (formData.customer_hours_to) customerHoursPart += ` - ${formatTime12h(formData.customer_hours_to)}`;
    }
    
    const szPart = size ? `Sz ${size}` : '';
    
    let message = '';
    switch (formData.task_type) {
      // Load out container EMCU8437680 from APM400, Pin#9482, Sz 40HQ, at 04/07/26 10:30AM - 11:00AM and deliver to C Yard
      case 'LOAD_OUT_DELIVER': {
        message = `Load out container ${container} from ${fromLoc || terminalCode}`;
        const frags = [];
        if (pin) frags.push(`Pin#${pin}`);
        if (szPart) frags.push(szPart);
        if (frags.length) message += `, ${frags.join(', ')}`;
        if (dateTimePart) message += `, at ${dateTimePart}`;
        if (toLoc) message += ` and deliver to ${toLoc}`;
        if (customerHoursPart) message += `, ${customerHoursPart}`;
        break;
      }
      // Empty out a Sz 40HQ, EG container from APM400 with Pin#9482 and deliver to C Yard
      case 'EMPTY_OUT_DELIVER': {
        const sizeSsl = [szPart, ssl].filter(Boolean).join(', ');
        message = `Empty out${sizeSsl ? ` a ${sizeSsl}` : ''} container from ${fromLoc || terminalCode}`;
        if (pin) message += ` with Pin#${pin}`;
        if (dateTimePart) message += `, at ${dateTimePart}`;
        if (toLoc) message += ` and deliver to ${toLoc}`;
        if (customerHoursPart) message += `, ${customerHoursPart}`;
        break;
      }
      // Return empty container EMCU8437680 Sz 40HQ, from Customer to C Yard
      case 'RETURN_EMPTY': {
        message = `Return empty container ${container}`;
        if (szPart) message += ` ${szPart}`;
        if (fromLoc) message += `, from ${fromLoc}`;
        if (toLoc) message += ` to ${toLoc}`;
        if (dateTimePart) message += `, at ${dateTimePart}`;
        if (customerHoursPart) message += `, ${customerHoursPart}`;
        break;
      }
      // Return load container EMCU8437680 Sz 40HQ, from Customer to Terminal
      case 'RETURN_LOAD': {
        message = `Return load container ${container}`;
        if (szPart) message += ` ${szPart}`;
        if (fromLoc) message += `, from ${fromLoc}`;
        if (toLoc) message += ` to ${toLoc}`;
        if (dateTimePart) message += `, at ${dateTimePart}`;
        if (customerHoursPart) message += `, ${customerHoursPart}`;
        break;
      }
      // Deliver LOAD container EMCU8437680, Sz 40HQ, from C Yard, to 5491 E Philadelphia St at 04/08/26 09:15PM
      case 'DELIVER_FROM_TO': {
        const loadType = formData.is_load_or_empty === 'LOAD' ? 'LOAD' : formData.is_load_or_empty === 'EMPTY' ? 'EMPTY' : '';
        message = `Deliver${loadType ? ` ${loadType}` : ''} container ${container}`;
        if (szPart) message += `, ${szPart}`;
        if (fromLoc) message += `, from ${fromLoc}`;
        if (toLoc) message += `, to ${toLoc}`;
        if (dateTimePart) message += ` at ${dateTimePart}`;
        if (customerHoursPart) message += `, ${customerHoursPart}`;
        break;
      }
      default:
        message = container;
        break;
    }
    
    if (reqs.length > 0) message += `\nREQ: ${reqs.join(', ')}`;
    
    return message.trim();
  };

  const handleCopyMessage = () => {
    if (dispatchMessage) {
      navigator.clipboard.writeText(dispatchMessage)
        .then(() => toast.success('Dispatch message copied to clipboard'))
        .catch(() => toast.error('Failed to copy to clipboard'));
    }
  };

  const handleSendDispatch = async () => {
    if (!formData.driver_id) {
      toast.error('Please select a driver');
      return;
    }
    if (!formData.task_type) {
      toast.error('Please select a task');
      return;
    }

    setLoading(true);
    try {
      // Determine destination type for status update
      let destinationType = '';
      if (formData.return_to_type === 'yard' || formData.deliver_to_type === 'yard') {
        destinationType = 'yard';
      } else if (formData.return_to_type === 'terminal' || formData.deliver_to_type === 'terminal') {
        destinationType = 'terminal';
      } else if (formData.return_to_type === 'customer_address' || formData.deliver_to_type === 'customer_address') {
        destinationType = 'customer';
      }
      
      // Create the dispatch
      const dispatchResponse = await createDispatchWithOverride((override) => api.post('/dispatches', {
        shipment_id: shipment.id,
        container_number: shipment.container_number,
        driver_id: formData.driver_id,
        phone_number: formData.phone_number,
        task: dispatchMessage,
        task_type: formData.task_type,
        destination: getLocationName(formData.deliver_to_type || formData.return_to_type, 
                                      formData.deliver_to_location || formData.return_to_location,
                                      formData.deliver_to_other || formData.return_to_other),
        destination_type: destinationType,
        dispatch_date: formData.dispatch_date,
        dispatch_time_from: formData.dispatch_time_from,
        dispatch_time_to: formData.dispatch_time_to,
        require_signature: formData.require_signature,
        require_seal_number: formData.require_seal_number,
        require_chassis_number: formData.require_chassis_number,
        require_container_picture: formData.require_container_picture,
        require_terminal_ticket: formData.require_terminal_ticket,
        delivery_type: formData.delivery_type || '',
        override_out_of_service: override,
      }));
      
      // Also send this dispatch as a message to the driver
      const selectedDriver = drivers.find(d => d.id === formData.driver_id);
      if (selectedDriver?.user_id) {
        try {
          await api.post('/messages', {
            receiver_id: selectedDriver.user_id,
            content: `📋 NEW DISPATCH:\n\n${dispatchMessage}`,
            message_type: 'dispatch'
          });
        } catch (msgError) {
          console.warn('Failed to send dispatch message:', msgError);
          // Don't fail the dispatch if message fails
        }
      }
      
      toast.success('Dispatch sent to driver');
      onDispatchCreated?.();
    } catch (error) {
      if (error?.handled) {
        toast.info('Dispatch cancelled');
      } else {
        toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Failed to create dispatch');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderLocationOptions = (prefix, currentType, currentLocation, currentOther) => (
    <div className="space-y-3">
      <div className="space-y-2">
        <Label className="text-slate-300 text-sm">Location Type</Label>
        <Select value={currentType} onValueChange={(v) => handleChange(`${prefix}_type`, v)}>
          <SelectTrigger className="bg-slate-950 border-slate-800 text-white h-9">
            <SelectValue placeholder="Select location type..." />
          </SelectTrigger>
          <SelectContent className="bg-slate-900 border-slate-800">
            <SelectItem value="customer_address" className="text-white">Customer Address</SelectItem>
            <SelectItem value="terminal" className="text-white">Terminal</SelectItem>
            <SelectItem value="yard" className="text-white">Yard</SelectItem>
            <SelectItem value="other" className="text-white">Other (Manual Input)</SelectItem>
          </SelectContent>
        </Select>
      </div>
      
      {currentType === 'terminal' && (
        <div className="space-y-2">
          <Label className="text-slate-300 text-sm">Select Terminal</Label>
          <Select value={currentLocation} onValueChange={(v) => handleChange(`${prefix}_location`, v)}>
            <SelectTrigger className="bg-slate-950 border-slate-800 text-white h-9">
              <SelectValue placeholder="Select terminal..." />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
              {terminals?.filter(t => t.id).length > 0 ? terminals.filter(t => t.id).map(t => (
                <SelectItem key={t.id} value={t.id} className="text-white">
                  {t.name || 'Terminal'} {t.code ? `(${t.code})` : ''}
                </SelectItem>
              )) : (
                <SelectItem value="no-terminals" disabled className="text-slate-500">
                  No terminals configured
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {currentType === 'yard' && (
        <div className="space-y-2">
          <Label className="text-slate-300 text-sm">Select Yard</Label>
          <Select value={currentLocation} onValueChange={(v) => handleChange(`${prefix}_location`, v)}>
            <SelectTrigger className="bg-slate-950 border-slate-800 text-white h-9">
              <SelectValue placeholder="Select yard..." />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
              {yards?.filter(y => y.id).length > 0 ? yards.filter(y => y.id).map(y => (
                <SelectItem key={y.id} value={y.id} className="text-white">{y.name || 'Yard'}</SelectItem>
              )) : (
                <SelectItem value="no-yards" disabled className="text-slate-500">
                  No yards configured
                </SelectItem>
              )}
            </SelectContent>
          </Select>
        </div>
      )}
      
      {currentType === 'customer_address' && (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Customer Address</Label>
            <Input
              value={getCustomerAddress()}
              disabled
              className="bg-slate-950 border-slate-800 text-slate-400 h-9 text-sm"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Receiving Hours</Label>
            <div className="flex items-center gap-2">
              <TimePicker
                value={formData.customer_hours_from}
                onChange={(time) => handleChange('customer_hours_from', time)}
                placeholder="From"
                className="w-32"
              />
              <span className="text-slate-400 text-sm">to</span>
              <TimePicker
                value={formData.customer_hours_to}
                onChange={(time) => handleChange('customer_hours_to', time)}
                placeholder="To"
                className="w-32"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">Address Remark</Label>
            <Input
              value={formData.customer_remark}
              onChange={(e) => handleChange('customer_remark', e.target.value)}
              placeholder="e.g. Gate code #1234, Dock 5, call on arrival..."
              className="bg-slate-950 border-slate-800 text-white h-9 text-sm"
            />
          </div>
        </div>
      )}
      
      {currentType === 'other' && (
        <div className="space-y-2">
          <Label className="text-slate-300 text-sm">Enter Location</Label>
          <Input
            value={currentOther}
            onChange={(e) => handleChange(`${prefix}_other`, e.target.value)}
            placeholder="Enter location manually..."
            className="bg-slate-950 border-slate-800 text-white h-9 text-sm"
          />
        </div>
      )}
    </div>
  );

  if (!shipment) return null;

  return (
    <div className="space-y-6">
      {/* Driver Selection */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label className="text-slate-300">DRIVER *</Label>
          <Select value={formData.driver_id} onValueChange={(v) => handleChange('driver_id', v)}>
            <SelectTrigger className="bg-slate-950 border-slate-800 text-white" data-testid="dispatch-driver-select">
              <SelectValue placeholder="Select driver..." />
            </SelectTrigger>
            <SelectContent className="bg-slate-900 border-slate-800 max-h-[200px]">
              {drivers?.map(d => (
                <SelectItem key={d.id} value={d.id} className="text-white">
                  {d.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label className="text-slate-300">PHONE NUMBER</Label>
          <Input
            value={formData.phone_number}
            onChange={(e) => handleChange('phone_number', e.target.value)}
            placeholder="Phone number"
            className="bg-slate-950 border-slate-800 text-white"
          />
        </div>
      </div>

      {/* Task Selection - Clickable Buttons */}
      <div className="space-y-3">
        <Label className="text-slate-300">TASK *</Label>
        <div className="flex flex-wrap gap-2">
          {TASK_TYPES.map(t => (
            <Button
              key={t.value}
              type="button"
              variant={formData.task_type === t.value ? "default" : "outline"}
              size="sm"
              onClick={() => handleChange('task_type', t.value)}
              className={`
                transition-all duration-200
                ${formData.task_type === t.value 
                  ? 'bg-blue-600 hover:bg-blue-500 text-white border-blue-600' 
                  : 'bg-slate-950 border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white hover:border-slate-600'
                }
              `}
              data-testid={`task-btn-${t.value.toLowerCase()}`}
            >
              {t.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Task-specific options */}
      {(formData.task_type === 'LOAD_OUT_DELIVER' || formData.task_type === 'EMPTY_OUT_DELIVER') && (
        <div className="p-4 bg-slate-800/50 rounded-lg space-y-4">
          <div className="text-sm text-slate-400">
            Terminal: <span className="text-white font-medium">{getTerminalName(shipment.terminal_id) || 'Not set'}</span>
          </div>
          <Label className="text-slate-300 font-medium">Deliver To</Label>
          {renderLocationOptions('deliver_to', formData.deliver_to_type, formData.deliver_to_location, formData.deliver_to_other)}
        </div>
      )}

      {(formData.task_type === 'RETURN_EMPTY' || formData.task_type === 'RETURN_LOAD') && (
        <div className="p-4 bg-slate-800/50 rounded-lg space-y-4">
          <div className="text-sm text-slate-400">
            Last Status: <span className="text-white font-medium">{shipment.status}</span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 font-medium mb-3 block">From</Label>
              {renderLocationOptions('return_from', formData.return_from_type, formData.return_from_location, formData.return_from_other)}
            </div>
            <div>
              <Label className="text-slate-300 font-medium mb-3 block">To</Label>
              {renderLocationOptions('return_to', formData.return_to_type, formData.return_to_location, formData.return_to_other)}
            </div>
          </div>
        </div>
      )}

      {formData.task_type === 'DELIVER_FROM_TO' && (
        <div className="p-4 bg-slate-800/50 rounded-lg space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300">Load or Empty?</Label>
            <Select value={formData.is_load_or_empty} onValueChange={(v) => handleChange('is_load_or_empty', v)}>
              <SelectTrigger className="bg-slate-950 border-slate-800 text-white">
                <SelectValue placeholder="Select..." />
              </SelectTrigger>
              <SelectContent className="bg-slate-900 border-slate-800">
                <SelectItem value="LOAD" className="text-white">Load</SelectItem>
                <SelectItem value="EMPTY" className="text-white">Empty</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-slate-300 font-medium mb-3 block">From</Label>
              {renderLocationOptions('return_from', formData.return_from_type, formData.return_from_location, formData.return_from_other)}
            </div>
            <div>
              <Label className="text-slate-300 font-medium mb-3 block">To</Label>
              {renderLocationOptions('return_to', formData.return_to_type, formData.return_to_location, formData.return_to_other)}
            </div>
          </div>
        </div>
      )}

      {/* Dispatch Time */}
      <div className="space-y-2">
        <Label className="text-slate-300">DISPATCH DATE & TIME</Label>
        <div className="flex items-center gap-2 flex-wrap">
          <Input
            type="date"
            value={formData.dispatch_date}
            onChange={(e) => handleChange('dispatch_date', e.target.value)}
            className="bg-slate-950 border-slate-800 text-white w-36"
          />
          <TimePicker
            value={formData.dispatch_time_from}
            onChange={(time) => handleChange('dispatch_time_from', time)}
            placeholder="From"
            className="w-32"
          />
          <span className="text-slate-400">TO</span>
          <TimePicker
            value={formData.dispatch_time_to}
            onChange={(time) => handleChange('dispatch_time_to', time)}
            placeholder="To"
            className="w-32"
          />
        </div>
        {shipment.appointment_date && (
          <p className="text-xs text-slate-500">
            Appointment: {new Date(shipment.appointment_date).toLocaleDateString()} {shipment.appointment_time_from || ''}
          </p>
        )}
      </div>

      {/* Delivery Type */}
      <div className="space-y-3">
        <Label className="text-slate-300 font-semibold">Delivery Type</Label>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={formData.delivery_type === 'LIVE_UNLOAD' ? "default" : "outline"}
            size="sm"
            onClick={() => handleChange('delivery_type', formData.delivery_type === 'LIVE_UNLOAD' ? '' : 'LIVE_UNLOAD')}
            className={`transition-all duration-200 ${formData.delivery_type === 'LIVE_UNLOAD' 
              ? 'bg-blue-600 hover:bg-blue-500 text-white border-blue-600' 
              : 'bg-slate-950 border-slate-700 text-blue-400 hover:bg-blue-950 hover:border-blue-800'}`}
          >
            {formData.delivery_type === 'LIVE_UNLOAD' && '✓ '}LIVE UNLOAD
          </Button>
          <Button
            type="button"
            variant={formData.delivery_type === 'DROP' ? "default" : "outline"}
            size="sm"
            onClick={() => handleChange('delivery_type', formData.delivery_type === 'DROP' ? '' : 'DROP')}
            className={`transition-all duration-200 ${formData.delivery_type === 'DROP' 
              ? 'bg-blue-600 hover:bg-blue-500 text-white border-blue-600' 
              : 'bg-slate-950 border-slate-700 text-blue-400 hover:bg-blue-950 hover:border-blue-800'}`}
          >
            {formData.delivery_type === 'DROP' && '✓ '}DROP
          </Button>
        </div>
      </div>

      {/* Additional Required Actions - Clickable Toggle Buttons */}
      <div className="space-y-3">
        <Label className="text-slate-300 font-semibold">Additional required action(s)</Label>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant={formData.require_signature ? "default" : "outline"}
            size="sm"
            onClick={() => handleChange('require_signature', !formData.require_signature)}
            className={`
              transition-all duration-200
              ${formData.require_signature 
                ? 'bg-red-600 hover:bg-red-500 text-white border-red-600' 
                : 'bg-slate-950 border-slate-700 text-red-400 hover:bg-red-950 hover:border-red-800'
              }
            `}
            data-testid="require-signature-btn"
          >
            {formData.require_signature && '✓ '}SIGNATURE (POD)
          </Button>
          
          <Button
            type="button"
            variant={formData.require_chassis_number ? "default" : "outline"}
            size="sm"
            onClick={() => handleChange('require_chassis_number', !formData.require_chassis_number)}
            className={`
              transition-all duration-200
              ${formData.require_chassis_number 
                ? 'bg-red-600 hover:bg-red-500 text-white border-red-600' 
                : 'bg-slate-950 border-slate-700 text-red-400 hover:bg-red-950 hover:border-red-800'
              }
            `}
            data-testid="require-chassis-btn"
          >
            {formData.require_chassis_number && '✓ '}CHASSIS NUMBER
          </Button>
          
          <Button
            type="button"
            variant={formData.require_seal_number ? "default" : "outline"}
            size="sm"
            onClick={() => handleChange('require_seal_number', !formData.require_seal_number)}
            className={`
              transition-all duration-200
              ${formData.require_seal_number 
                ? 'bg-red-600 hover:bg-red-500 text-white border-red-600' 
                : 'bg-slate-950 border-slate-700 text-red-400 hover:bg-red-950 hover:border-red-800'
              }
            `}
            data-testid="require-seal-btn"
          >
            {formData.require_seal_number && '✓ '}SEAL NUMBER
          </Button>
          
          <Button
            type="button"
            variant={formData.require_container_picture ? "default" : "outline"}
            size="sm"
            onClick={() => handleChange('require_container_picture', !formData.require_container_picture)}
            className={`
              transition-all duration-200
              ${formData.require_container_picture 
                ? 'bg-red-600 hover:bg-red-500 text-white border-red-600' 
                : 'bg-slate-950 border-slate-700 text-red-400 hover:bg-red-950 hover:border-red-800'
              }
            `}
            data-testid="require-picture-btn"
          >
            {formData.require_container_picture && '✓ '}CONTAINER PICTURE
          </Button>
          
          <Button
            type="button"
            variant={formData.require_terminal_ticket ? "default" : "outline"}
            size="sm"
            onClick={() => handleChange('require_terminal_ticket', !formData.require_terminal_ticket)}
            className={`
              transition-all duration-200
              ${formData.require_terminal_ticket 
                ? 'bg-red-600 hover:bg-red-500 text-white border-red-600' 
                : 'bg-slate-950 border-slate-700 text-red-400 hover:bg-red-950 hover:border-red-800'
              }
            `}
            data-testid="require-ticket-btn"
          >
            {formData.require_terminal_ticket && '✓ '}TERMINAL TICKET
          </Button>
        </div>
      </div>

      {/* Status Triggers - REMOVED per user request */}

      {/* Generated Dispatch Message */}
      {dispatchMessage && (
        <div className="space-y-2">
          <Label className="text-slate-300 font-semibold flex items-center gap-2">
            <MessageSquare className="h-4 w-4" />
            Generated Dispatch Message
          </Label>
          <Textarea
            value={dispatchMessage}
            readOnly
            className="bg-slate-950 border-slate-800 text-white font-mono text-sm min-h-[80px]"
            data-testid="dispatch-message-preview"
          />
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-3 pt-4 border-t border-slate-800">
        <Button
          onClick={handleCopyMessage}
          disabled={!dispatchMessage}
          variant="outline"
          className="border-slate-700"
          data-testid="copy-dispatch-text-btn"
        >
          <Copy className="h-4 w-4 mr-2" />
          Copy Text
        </Button>
        <Button
          onClick={handleSendDispatch}
          disabled={loading || !formData.driver_id || !formData.task_type}
          className="bg-blue-600 hover:bg-blue-500"
          data-testid="send-dispatch-to-driver-btn"
        >
          {loading && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          <Send className="h-4 w-4 mr-2" />
          Send to Driver
        </Button>
      </div>
    </div>
  );
};

export default DispatchScheduleTab;
